// src/components/DownloadModal.js
import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import downloadService from '../services/downloadService';
import apiService from '../services/api';

const DownloadModal = ({ show, onHide, download }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (download.requiresEmail && !email) {
      setError('Email is required');
      return;
    }
    
    if (download.requiresEmail && !consent) {
      setError('Please agree to receive updates');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Track the download
      await downloadService.trackDownload(download.id);
      
      // If email is required, submit it to your API
      if (download.requiresEmail && email) {
        await apiService.submitForm('download', {
          email,
          name,
          downloadId: download.id,
          downloadTitle: download.title
        });
      }
      
      // Trigger the download
      const link = document.createElement('a');
      link.href = download.fileUrl;
      link.download = download.fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess(true);
      
      // Close modal after a delay
      setTimeout(() => {
        onHide();
        // Reset form
        setEmail('');
        setName('');
        setConsent(false);
        setSuccess(false);
      }, 2000);
      
    } catch (err) {
      console.error('Error processing download:', err);
      setError('Failed to process download. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDirectDownload = () => {
    // Track the download
    downloadService.trackDownload(download.id);
    
    // Trigger the download
    const link = document.createElement('a');
    link.href = download.fileUrl;
    link.download = download.fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {download.requiresEmail !== false ? 'Get Your Free Resource' : 'Download Resource'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="download-modal-content">
          <div className="download-info">
            <h4>{download.title}</h4>
            <p className="text-muted">{download.description}</p>
          </div>

          {success ? (
            <Alert variant="success">
              <i className="fas fa-check-circle me-2"></i>
              Your download has started! Check your downloads folder.
            </Alert>
          ) : (
            <>
              {error && <Alert variant="danger">{error}</Alert>}

              {download.requiresEmail !== false ? (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                    <Form.Text className="text-muted">
                      We'll send you the download link and occasional updates
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="consent-checkbox"
                      label="I agree to receive emails about business resources and tips"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loading}
                      size="lg"
                    >
                      {loading ? 'Processing...' : (download.buttonText || 'Download Now')}
                    </Button>
                  </div>

                  <p className="text-center mt-3 mb-0 text-muted small">
                    <i className="fas fa-lock me-1"></i>
                    Your information is secure and will never be shared
                  </p>
                </Form>
              ) : (
                <div className="text-center">
                  <p className="mb-4">Click the button below to download your resource.</p>
                  <Button
                    variant="primary"
                    onClick={handleDirectDownload}
                    size="lg"
                    className="px-5"
                  >
                    {download.buttonText || 'Download Now'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DownloadModal;
